import { useCallback, useEffect, useMemo, useRef } from "react";
import _ from "underscore";

import { PERSISTENCE_DEBOUNCE_MS } from "../constants";
import type { WorkbenchState } from "../types";
import { saveWorkbench, toPersistedWorkbench } from "../utils/storage";

/**
 * Mirror the persistable slice of the workbench into IndexedDB.
 *
 * SQL edits are debounced because they arrive on every keystroke; the write is
 * also flushed when the page is hidden, so closing the tab doesn't lose the
 * last few hundred milliseconds of typing.
 */
export function useAdhocPersistence(
  state: WorkbenchState,
  userId: number | undefined | null,
): { flush: () => void } {
  const stateRef = useRef(state);
  stateRef.current = state;

  const save = useMemo(
    () =>
      _.debounce((nextState: WorkbenchState, id: number | undefined | null) => {
        // Fire and forget: a failed write is not worth interrupting a query over.
        void saveWorkbench(id, toPersistedWorkbench(nextState, Date.now()));
      }, PERSISTENCE_DEBOUNCE_MS),
    [],
  );

  // The first state we see is the one we just read back out of storage, so
  // writing it again would only burn a write of the whole payload.
  const isHydrationRender = useRef(true);
  useEffect(() => {
    if (isHydrationRender.current) {
      isHydrationRender.current = false;
      return;
    }
    save(state, userId);
  }, [save, state, userId]);

  const flush = useCallback(() => {
    save.cancel();
    void saveWorkbench(
      userId,
      toPersistedWorkbench(stateRef.current, Date.now()),
    );
  }, [save, userId]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        flush();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", flush);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", flush);
      flush();
    };
  }, [flush]);

  return { flush };
}
